/**
 * Form field + text input.
 *
 * Anatomy: 12px label at `font-[630]`, 32px control, `rounded-small` (4px), 1px
 * `--c9-border-input`, focus swaps the border to `--c9-border-focused`. Error
 * state uses `--c9-border-danger` and shows the message below.
 */
import { Show, children, createUniqueId, splitProps, type JSX, type ParentProps } from "solid-js";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export function FormField(
  props: ParentProps<{
    label?: string;
    /** Rendered under the control; hidden while an error is showing. */
    hint?: JSX.Element;
    error?: string;
    required?: boolean;
    for?: string;
    class?: string;
  }>,
): JSX.Element {
  const hint = children(() => props.hint);
  return (
    <div class={cn("flex min-w-0 flex-col gap-050", props.class)}>
      <Show when={props.label}>
        <label
          for={props.for}
          class="text-body-s font-[630] text-[color:var(--c9-text-subtle)]"
        >
          {props.label}
          <Show when={props.required}>
            <span aria-hidden="true" class="ml-025 text-[color:var(--c9-text-danger)]">
              *
            </span>
          </Show>
        </label>
      </Show>

      {props.children}

      <Show
        when={props.error}
        fallback={
          <Show when={hint()}>
            <p class="text-body-s text-[color:var(--c9-text-subtlest)]">{hint()}</p>
          </Show>
        }
      >
        {(message) => (
          <p role="alert" class="text-body-s text-[color:var(--c9-text-danger)]">
            {message()}
          </p>
        )}
      </Show>
    </div>
  );
}

export interface TextInputProps extends JSX.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput(props: TextInputProps): JSX.Element {
  const [local, rest] = splitProps(props, ["invalid", "class"]);
  return (
    <input
      {...rest}
      aria-invalid={local.invalid ? "true" : undefined}
      class={cn(
        "h-8 w-full min-w-0 rounded-small border bg-[var(--c9-bg-input)] px-100",
        "text-body text-[color:var(--c9-text)] placeholder:text-[color:var(--c9-text-subtlest)]",
        "transition-colors",
        local.invalid
          ? "border-[color:var(--c9-border-danger)]"
          : "border-[color:var(--c9-border-input)] hover:border-[color:var(--c9-border-bold)]",
        "disabled:cursor-not-allowed disabled:bg-[var(--c9-bg-disabled)] disabled:text-[color:var(--c9-text-disabled)]",
        focusRing,
        local.class,
      )}
    />
  );
}

/** Convenience: a labelled text input with the id wired for accessibility. */
export function TextField(
  props: TextInputProps & { label?: string; hint?: JSX.Element; error?: string },
): JSX.Element {
  const id = createUniqueId();
  const [local, inputProps] = splitProps(props, ["label", "hint", "error", "class"]);
  return (
    <FormField
      label={local.label}
      hint={local.hint}
      error={local.error}
      required={inputProps.required}
      for={id}
      class={local.class}
    >
      <TextInput id={id} invalid={!!local.error} {...inputProps} />
    </FormField>
  );
}
